/**
 * Memory strategies (AgentCore-inspired)
 *
 * Maps Losi durable memories onto the same four long-term strategies used by
 * Amazon Bedrock AgentCore Memory, without depending on AgentCore.
 *
 * Short-term continuity stays in chat history / session state.
 * Long-term records live in user_memories / chat_memories and are retrieved
 * with strategy-aware ranking before prompt injection.
 */

export const MEMORY_STRATEGIES = [
  'preference',
  'semantic',
  'summary',
  'episodic'
] as const

export type MemoryStrategy = (typeof MEMORY_STRATEGIES)[number]

/** Default retrieval knobs aligned with AgentCore harness defaults. */
export const MEMORY_RETRIEVAL_DEFAULTS = {
  topK: 10,
  minRelevanceScore: 0.2
} as const

const STRATEGY_ALIASES: Record<string, MemoryStrategy> = {
  preference: 'preference',
  preferences: 'preference',
  user_preference: 'preference',
  userpreference: 'preference',
  technical: 'preference',
  learning: 'preference',
  communication: 'preference',
  work: 'preference',
  workflow: 'preference',
  semantic: 'semantic',
  fact: 'semantic',
  facts: 'semantic',
  reference: 'semantic',
  project: 'semantic',
  team: 'semantic',
  constraint: 'semantic',
  summary: 'summary',
  summarization: 'summary',
  session_summary: 'summary',
  episodic: 'episodic',
  episode: 'episodic',
  event: 'episodic',
  auto: 'semantic',
  general: 'semantic'
}

const STRATEGY_PROMPT_LABEL: Record<MemoryStrategy, string> = {
  preference: 'Preferences',
  semantic: 'Facts',
  summary: 'Summaries',
  episodic: 'Episodes'
}

// --------------

export function normalizeMemoryStrategy(
  value: string | null | undefined
): MemoryStrategy {
  if (!value) return 'semantic'
  const key = value.trim().toLowerCase().replace(/[\s-]+/g, '_')
  return STRATEGY_ALIASES[key] ?? 'semantic'
}

// --------------

export function memoryStrategyLabel(strategy: MemoryStrategy): string {
  return STRATEGY_PROMPT_LABEL[strategy]
}

// --------------

/**
 * Lightweight classifier for online MemoryTool writes when the caller does
 * not supply a strategy. Prefer explicit types from extraction when present.
 */
export function classifyMemoryStrategy(content: string): MemoryStrategy {
  const text = content.trim().toLowerCase()
  if (!text) return 'semantic'

  if (
    /\b(prefer|preference|always|never|don't|do not|likes?|hates?|style|tone|format)\b/.test(
      text
    )
  ) {
    return 'preference'
  }

  if (
    /\b(summary|summarize|recap|in this (chat|session|conversation)|decided to|agreed to)\b/.test(
      text
    )
  ) {
    return 'summary'
  }

  if (
    /\b(yesterday|today|last week|on \d|when we|episode|incident|shipped|launched|happened)\b/.test(
      text
    )
  ) {
    return 'episodic'
  }

  return 'semantic'
}

// --------------

export function memoryNamespaceForActor(input: {
  strategy: MemoryStrategy
  actorId: string
  sessionId?: string | null
}): string {
  const actor = input.actorId.trim() || 'unknown'
  const session = input.sessionId?.trim()

  switch (input.strategy) {
    case 'preference':
      return `/preferences/${actor}/`
    case 'semantic':
      return `/facts/${actor}/`
    case 'summary':
      return session
        ? `/summaries/${actor}/${session}/`
        : `/summaries/${actor}/`
    case 'episodic':
      return session
        ? `/episodes/${actor}/${session}/`
        : `/episodes/${actor}/`
  }
}
